import type { ReactNode } from "react";
import { CLIENT_ARTIFACT_INTEGRITY, type ClientArtifactServiceWeekHeader } from "../data/client-artifact-contract";
import "../styles/client-artifact.css";

export function clientArtifactDay(value: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(new Date(`${value}T12:00:00Z`));
}
export function clientArtifactInstant(value: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZoneName: "shortOffset" }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  // Browsers differ in date/time separator words. Fixed punctuation preserves print parity.
  return `${part("month")} ${part("day")}, ${part("year")}, ${part("hour")}:${part("minute")} ${part("timeZoneName")}`;
}
function lastDay(exclusive: string): string {
  const value = new Date(`${exclusive}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() - 1);
  return value.toISOString().slice(0, 10);
}

/** The owning exact-key parser validates printable headers before rendering. */
export function ClientArtifactHeader({ title, header, approvedAt, children }: {
  title: string; header: ClientArtifactServiceWeekHeader; approvedAt: string; children?: ReactNode;
}) {
  return <header className="pos-paper-header">
    <div className="pos-paper-title"><p>{header.providerLabel}</p><h1>{title}</h1></div>
    <dl className="pos-paper-meta">
      <div><dt>Prepared for</dt><dd>{header.clientLabel}</dd></div>
      <div><dt>Site</dt><dd>{header.siteLabel}</dd></div>
      <div><dt>Period</dt><dd>{clientArtifactDay(header.weekStart)} to {clientArtifactDay(lastDay(header.weekEndExclusive))}</dd></div>
      <div><dt>Site timezone</dt><dd>{header.timeZone}</dd></div>
      <div><dt>Prepared by</dt><dd>{header.preparedByLabel}</dd></div>
      <div><dt>Approval recorded</dt><dd>{clientArtifactInstant(approvedAt, header.timeZone)}</dd></div>
    </dl>
    {children}
  </header>;
}
export function ClientArtifactFooter({ header, page, total, children }: {
  header: Pick<ClientArtifactServiceWeekHeader, "generatedAt" | "dataThroughAt" | "timeZone">;
  page: number; total: number; children?: ReactNode;
}) {
  return <footer className="pos-paper-footer">
    <p className="pos-paper-legend"><strong>✓</strong> device-verified at the time shown · <strong>s</strong> supervisor-set; reason on file · <strong>e</strong> officer-entered; no device proof · <strong>-</strong> missing · <strong>!</strong> exception noted</p>
    <p>{CLIENT_ARTIFACT_INTEGRITY}</p>
    {children}
    <div className="pos-paper-footline"><span>Generated {clientArtifactInstant(header.generatedAt, header.timeZone)}<br />Data through {clientArtifactInstant(header.dataThroughAt, header.timeZone)}</span><span>Page {page} of {total}</span></div>
  </footer>;
}

/** No transport envelope, private revision, release capability or browser-print activation. */
export function ClientArtifactPage({ title, header, approvedAt, summary, children, pagination, recordsNote }: {
  title: string; header: ClientArtifactServiceWeekHeader; approvedAt: string; summary: ReactNode;
  children: ReactNode; pagination: { page: number; total: number }; recordsNote: ReactNode;
}) {
  return <article className="pos-paper" aria-label={`${title} page ${pagination.page} of ${pagination.total}`}>
    <ClientArtifactHeader title={title} header={header} approvedAt={approvedAt}>{summary}</ClientArtifactHeader>
    <div className="pos-paper-content">{children}</div>
    <ClientArtifactFooter header={header} page={pagination.page} total={pagination.total}>{recordsNote}</ClientArtifactFooter>
  </article>;
}
